// src/context.ts
import { createContext, createElement, useContext } from "react";
var ApiClientContext = createContext(null);
function ApiClientProvider({ value, children }) {
  return createElement(ApiClientContext.Provider, { value }, children);
}
function useApiClient() {
  const client = useContext(ApiClientContext);
  if (!client) {
    throw new Error("useApiClient must be used within an <ApiClientProvider>");
  }
  return client;
}

// src/keys.ts
var queryKeys = {
  locations: {
    all: () => ["locations"],
    nearby: (locationId) => ["locations", "nearby", locationId],
    search: (query, country) => ["locations", "search", query, country],
    nearbyCoords: (lat, lng, radius) => ["locations", "nearby-coords", lat, lng, radius]
  },
  weather: {
    batch: () => ["weather", "batch"],
    current: (locationId) => ["weather", "current", locationId]
  },
  stories: {
    daily: (locationId, humor) => ["stories", "daily", locationId, humor],
    all: (locationId, type, humor) => ["stories", locationId, type, humor],
    recent: (limit) => ["stories", "recent", limit],
    crossLocation: (excludeLocationId) => ["stories", "cross-location", excludeLocationId],
    counts: () => ["stories", "counts"]
  },
  airQuality: {
    current: (locationId) => ["air-quality", locationId]
  },
  willItRain: {
    check: (locationId, time) => ["will-it-rain", locationId, time]
  },
  forecast: {
    weekly: (locationId) => ["forecast", "weekly", locationId],
    hourly: (locationId) => ["forecast", "hourly", locationId]
  },
  onThisDay: {
    data: (locationId, date) => ["on-this-day", locationId, date]
  },
  historicalCalendar: {
    data: (locationId, month, year) => ["historical-calendar", locationId, month, year]
  },
  weatherEvents: {
    data: (locationId) => ["weather-events", locationId]
  },
  cloud: {
    identify: () => ["cloud", "identify"]
  },
  astronomical: {
    data: (locationId) => ["astronomical", locationId]
  },
  rainSummary: {
    data: (locationId) => ["rain-summary", locationId]
  },
  ads: {
    feed: (locationId) => ["ads", "feed", locationId]
  },
  streaks: {
    active: (locationId) => ["streaks", "active", locationId],
    averages: (locationId, month) => ["streaks", "averages", locationId, month],
    leaderboard: (streakType) => ["streaks", "leaderboard", streakType],
    predictions: (locationId) => ["streaks", "predictions", locationId],
    calendar: (locationId, year, month) => ["streaks", "calendar", locationId, year, month]
  }
};

// src/useLocations.ts
import { useQuery } from "@tanstack/react-query";
function useLocations() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.locations.all(),
    queryFn: async () => {
      const response = await api.get("/weather-data/locations");
      return response.data ?? [];
    },
    staleTime: 30 * 60 * 1e3
    // 30 minutes
  });
}

// src/useWeatherBatch.ts
import { useQuery as useQuery2 } from "@tanstack/react-query";
function useWeatherBatch(options) {
  const api = useApiClient();
  return useQuery2({
    queryKey: queryKeys.weather.batch(),
    queryFn: async () => {
      const response = await api.get("/weather-data/weather-batch");
      return response.data ?? {};
    },
    staleTime: 5 * 60 * 1e3,
    enabled: options?.enabled
  });
}

// src/useStories.ts
import { useQuery as useQuery3 } from "@tanstack/react-query";
function useStories(locationId, options) {
  const api = useApiClient();
  const { humor, type, enabled = true, staleTime = 5 * 60 * 1e3 } = options ?? {};
  return useQuery3({
    queryKey: type ? queryKeys.stories.all(locationId, type, humor) : queryKeys.stories.daily(locationId, humor),
    queryFn: async () => {
      const params = {
        locationId,
        humor: humor?.toString(),
        type: type === "all" ? void 0 : type
      };
      const endpoint = type ? "/stories" : "/stories/daily";
      const response = await api.get(endpoint, params);
      return response.data ?? [];
    },
    staleTime,
    enabled: enabled && !!locationId
  });
}

// src/useInfiniteStories.ts
import { useInfiniteQuery } from "@tanstack/react-query";
var PAGE_SIZE = 10;
function useInfiniteStories(locationId, options) {
  const api = useApiClient();
  const { humor, enabled = true } = options ?? {};
  return useInfiniteQuery({
    queryKey: ["stories", "infinite", locationId, humor],
    queryFn: async ({ pageParam = 0 }) => {
      const params = {
        locationId,
        offset: String(pageParam),
        limit: String(PAGE_SIZE)
      };
      if (humor) params.humor = "true";
      const response = await api.get("/stories/daily", params);
      const stories = response.data ?? [];
      return { stories, offset: pageParam };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.stories.length < PAGE_SIZE) return void 0;
      return lastPage.offset + PAGE_SIZE;
    },
    enabled: enabled && !!locationId,
    staleTime: 5 * 60 * 1e3
  });
}

// src/useRecentStories.ts
import { useQuery as useQuery4 } from "@tanstack/react-query";
function useRecentStories(options) {
  const api = useApiClient();
  const { limit = 10, enabled = true } = options ?? {};
  return useQuery4({
    queryKey: queryKeys.stories.recent(limit),
    queryFn: async () => {
      const response = await api.get("/stories/recent", { limit: limit.toString() });
      return response.data ?? [];
    },
    staleTime: 30 * 1e3,
    enabled
  });
}

// src/useCrossLocationStories.ts
import { useQuery as useQuery5 } from "@tanstack/react-query";
function useCrossLocationStories(options) {
  const api = useApiClient();
  const { excludeLocationId, enabled = true } = options ?? {};
  return useQuery5({
    queryKey: queryKeys.stories.crossLocation(excludeLocationId),
    queryFn: async () => {
      const params = {
        excludeLocationId
      };
      const response = await api.get("/stories/cross-location", params);
      return response.data ?? [];
    },
    staleTime: 5 * 60 * 1e3,
    enabled
  });
}

// src/useStoryCounts.ts
import { useQuery as useQuery6 } from "@tanstack/react-query";
function useStoryCounts(options) {
  const api = useApiClient();
  const { enabled = true } = options ?? {};
  return useQuery6({
    queryKey: queryKeys.stories.counts(),
    queryFn: async () => {
      const response = await api.get("/stories/counts");
      return response.data ?? {};
    },
    staleTime: 5 * 60 * 1e3,
    enabled
  });
}

// src/useCurrentWeather.ts
import { useQuery as useQuery7 } from "@tanstack/react-query";
function useCurrentWeather(locationId, options) {
  const api = useApiClient();
  return useQuery7({
    queryKey: queryKeys.weather.current(locationId),
    queryFn: async () => {
      const response = await api.get("/weather/current", { locationId });
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useAirQuality.ts
import { useQuery as useQuery8 } from "@tanstack/react-query";
function useAirQuality(locationId, options) {
  const api = useApiClient();
  return useQuery8({
    queryKey: queryKeys.airQuality.current(locationId),
    queryFn: async () => {
      const response = await api.get(`/weather-data/air-quality/${locationId}`);
      return response.data ?? null;
    },
    staleTime: 10 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useWillItRain.ts
import { useQuery as useQuery9 } from "@tanstack/react-query";
function useWillItRain(locationId, time, options) {
  const api = useApiClient();
  return useQuery9({
    queryKey: queryKeys.willItRain.check(locationId, time),
    queryFn: async () => {
      const params = { time };
      const response = await api.get(
        `/weather-data/will-it-rain/${locationId}`,
        params
      );
      return response.data ?? null;
    },
    staleTime: 5 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useRainSummary.ts
import { useQuery as useQuery10 } from "@tanstack/react-query";
function useRainSummary(locationId, options) {
  const api = useApiClient();
  return useQuery10({
    queryKey: queryKeys.rainSummary.data(locationId),
    queryFn: async () => {
      const response = await api.get(
        `/weather-data/rain-summary/${locationId}`
      );
      return response.data ?? null;
    },
    staleTime: 15 * 60 * 1e3,
    // 15 min — rain totals don't change fast
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useForecast.ts
import { useQuery as useQuery11 } from "@tanstack/react-query";
function useWeeklyForecast(locationId, options) {
  const api = useApiClient();
  const days = options?.days;
  return useQuery11({
    queryKey: [...queryKeys.forecast.weekly(locationId), days],
    queryFn: async () => {
      const params = {
        days: days ? String(days) : void 0
      };
      const response = await api.get(
        `/weather-data/forecast/${locationId}`,
        params
      );
      return response.data ?? null;
    },
    staleTime: 15 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}
function useHourlyForecast(locationId, options) {
  const api = useApiClient();
  const hours = options?.hours;
  return useQuery11({
    queryKey: [...queryKeys.forecast.hourly(locationId), hours],
    queryFn: async () => {
      const params = {
        hours: hours ? String(hours) : void 0
      };
      const response = await api.get(
        `/weather-data/hourly/${locationId}`,
        params
      );
      return response.data ?? null;
    },
    staleTime: 15 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useOnThisDay.ts
import { useQuery as useQuery12 } from "@tanstack/react-query";
function useOnThisDay(locationId, date, options) {
  const api = useApiClient();
  return useQuery12({
    queryKey: queryKeys.onThisDay.data(locationId, date),
    queryFn: async () => {
      const params = { date };
      const response = await api.get(
        `/historical/on-this-day/${locationId}`,
        params
      );
      return response.data ?? null;
    },
    staleTime: 30 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useHistoricalCalendar.ts
import { useQuery as useQuery13 } from "@tanstack/react-query";
function useHistoricalCalendar(locationId, month, year, options) {
  const api = useApiClient();
  return useQuery13({
    queryKey: queryKeys.historicalCalendar.data(locationId, month, year),
    queryFn: async () => {
      const response = await api.get(
        `/historical/calendar/${locationId}`,
        { month, year }
      );
      return response.data ?? null;
    },
    staleTime: 60 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useWeatherEvents.ts
import { useQuery as useQuery14 } from "@tanstack/react-query";
function useWeatherEvents(locationId, options) {
  const api = useApiClient();
  return useQuery14({
    queryKey: queryKeys.weatherEvents.data(locationId),
    queryFn: async () => {
      const response = await api.get(
        `/historical/events/${locationId}`
      );
      return response.data ?? null;
    },
    staleTime: 24 * 60 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useCompareDates.ts
import { useQuery as useQuery15 } from "@tanstack/react-query";
function useCompareDates(locationId, date1, date2, options) {
  const api = useApiClient();
  return useQuery15({
    queryKey: ["compare-dates", locationId, date1, date2],
    queryFn: async () => {
      const response = await api.get(
        `/historical/compare-dates/${locationId}`,
        { date1, date2 }
      );
      return response.data ?? null;
    },
    staleTime: 60 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId && !!date1 && !!date2
  });
}

// src/useCloudIdentify.ts
import { useMutation } from "@tanstack/react-query";
function useCloudIdentify() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async (imageData) => {
      const response = await api.post("/cloud/identify", imageData);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    }
  });
}

// src/useActiveStreaks.ts
import { useQuery as useQuery16 } from "@tanstack/react-query";
function useActiveStreaks(locationId, options) {
  const api = useApiClient();
  return useQuery16({
    queryKey: queryKeys.streaks.active(locationId),
    queryFn: async () => {
      const response = await api.get(
        `/streaks/${locationId}`
      );
      return response.data ?? [];
    },
    staleTime: 10 * 60 * 1e3,
    enabled: (options?.enabled ?? true) && !!locationId
  });
}
function useStreakAverages(locationId, month, options) {
  const api = useApiClient();
  return useQuery16({
    queryKey: queryKeys.streaks.averages(locationId, month),
    queryFn: async () => {
      const response = await api.get(
        `/streaks/${locationId}/averages`,
        { month: String(month) }
      );
      return response.data?.averages ?? {};
    },
    staleTime: 30 * 60 * 1e3,
    // 30 min — historical averages are stable
    enabled: (options?.enabled ?? true) && !!locationId
  });
}
function useStreakPredictions(locationId, options) {
  const api = useApiClient();
  return useQuery16({
    queryKey: queryKeys.streaks.predictions(locationId),
    queryFn: async () => {
      const response = await api.get(
        `/streaks/${locationId}/predictions`
      );
      return response.data ?? {};
    },
    staleTime: 30 * 60 * 1e3,
    // 30 min — forecast-based
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useStreakLeaderboard.ts
import { useQuery as useQuery17 } from "@tanstack/react-query";
function useStreakLeaderboard(streakType, options) {
  const api = useApiClient();
  return useQuery17({
    queryKey: queryKeys.streaks.leaderboard(streakType),
    queryFn: async () => {
      const params = {};
      if (streakType) params.type = streakType;
      const response = await api.get(
        "/streaks/leaderboard",
        params
      );
      return response.data ?? [];
    },
    staleTime: 10 * 60 * 1e3,
    enabled: options?.enabled !== false
  });
}

// src/useStreakCalendar.ts
import { useQuery as useQuery18 } from "@tanstack/react-query";
function useStreakCalendar(locationId, year, month, options) {
  const api = useApiClient();
  return useQuery18({
    queryKey: queryKeys.streaks.calendar(locationId, year, month),
    queryFn: async () => {
      const response = await api.get(
        `/streaks/${locationId}/calendar`,
        { year: String(year), month: String(month) }
      );
      return response.data ?? { year, month, days: [] };
    },
    staleTime: 60 * 60 * 1e3,
    // 1 hour — historical data is stable
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useAstronomical.ts
import { useQuery as useQuery19 } from "@tanstack/react-query";
import { getClientAstronomicalData } from "@weather-story/shared-utils";
function useAstronomical(locationId, options) {
  const api = useApiClient();
  return useQuery19({
    queryKey: queryKeys.astronomical.data(locationId),
    queryFn: async () => {
      try {
        const response = await api.get(
          `/weather-data/astronomical/${locationId}`
        );
        return response.data ?? null;
      } catch {
        if (options?.latitude == null) return null;
        const now = /* @__PURE__ */ new Date();
        const hemisphere = options.latitude < 0 ? "southern" : "northern";
        const computed = getClientAstronomicalData(now, hemisphere);
        return {
          moon: computed.moon,
          nextEvent: {
            ...computed.nextEvent,
            date: computed.nextEvent.date.toISOString()
          },
          season: computed.season,
          dayOfYear: computed.dayOfYear,
          isLeapYear: computed.isLeapYear
          // dayLength omitted — requires sunrise/sunset from API
        };
      }
    },
    staleTime: 60 * 60 * 1e3,
    // 1 hour — astronomical data changes slowly
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useNearbyLocations.ts
import { useQuery as useQuery20 } from "@tanstack/react-query";
function useNearbyLocations(locationId, options) {
  const api = useApiClient();
  return useQuery20({
    queryKey: queryKeys.locations.nearby(locationId),
    queryFn: async () => {
      const response = await api.get(
        `/weather-data/nearby/${locationId}`
      );
      return response.data ?? [];
    },
    staleTime: 60 * 60 * 1e3,
    // 1 hour — location data is static
    enabled: (options?.enabled ?? true) && !!locationId
  });
}

// src/useLocationSearch.ts
import { useQuery as useQuery21 } from "@tanstack/react-query";
function useLocationSearch(query, options) {
  const api = useApiClient();
  const limit = options?.limit ?? 15;
  const country = options?.country;
  return useQuery21({
    queryKey: queryKeys.locations.search(query, country),
    queryFn: async () => {
      const params = {
        q: query,
        limit,
        country
      };
      const response = await api.get("/locations/search", params);
      return response.data ?? [];
    },
    staleTime: 5 * 60 * 1e3,
    // 5 minutes
    enabled: (options?.enabled ?? true) && query.length >= 2
  });
}

// src/useNearbySearch.ts
import { useQuery as useQuery22 } from "@tanstack/react-query";
function useNearbySearch(coords, options) {
  const api = useApiClient();
  const radius = options?.radius ?? 100;
  return useQuery22({
    queryKey: queryKeys.locations.nearbyCoords(coords?.lat ?? 0, coords?.lng ?? 0, radius),
    queryFn: async () => {
      if (!coords) return [];
      const params = {
        lat: coords.lat,
        lng: coords.lng,
        radius
      };
      const response = await api.get("/locations/nearby", params);
      return response.data ?? [];
    },
    staleTime: 10 * 60 * 1e3,
    // 10 minutes
    enabled: (options?.enabled ?? true) && coords !== null
  });
}

// src/useGeocodingSearch.ts
import { useQuery as useQuery23 } from "@tanstack/react-query";
function useGeocodingSearch(query, options) {
  const api = useApiClient();
  const limit = options?.limit ?? 10;
  const country = options?.country ?? "AU";
  return useQuery23({
    queryKey: ["geocoding", "search", query, country],
    queryFn: async () => {
      const params = {
        q: query,
        country,
        limit
      };
      const response = await api.get("/locations/geocode", params);
      return response.data ?? [];
    },
    staleTime: 10 * 60 * 1e3,
    // 10 minutes
    enabled: (options?.enabled ?? true) && query.length >= 2
  });
}

// src/useAds.ts
import { useQuery as useQuery24 } from "@tanstack/react-query";
function useAds(options) {
  const api = useApiClient();
  const { locationId, conditions, limit = 3, enabled = true } = options ?? {};
  return useQuery24({
    queryKey: queryKeys.ads.feed(locationId),
    queryFn: async () => {
      const params = {
        locationId,
        conditions,
        limit: limit.toString()
      };
      const response = await api.get("/ads/feed", params);
      return response.data ?? [];
    },
    staleTime: 10 * 60 * 1e3,
    // 10 min — ads rotate less frequently
    enabled
  });
}
export {
  ApiClientProvider,
  queryKeys,
  useActiveStreaks,
  useAds,
  useAirQuality,
  useApiClient,
  useAstronomical,
  useCloudIdentify,
  useCompareDates,
  useCrossLocationStories,
  useCurrentWeather,
  useGeocodingSearch,
  useHistoricalCalendar,
  useHourlyForecast,
  useInfiniteStories,
  useLocationSearch,
  useLocations,
  useNearbyLocations,
  useNearbySearch,
  useOnThisDay,
  useRainSummary,
  useRecentStories,
  useStories,
  useStoryCounts,
  useStreakAverages,
  useStreakCalendar,
  useStreakLeaderboard,
  useStreakPredictions,
  useWeatherBatch,
  useWeatherEvents,
  useWeeklyForecast,
  useWillItRain
};
